/**
 * API Configuration
 * Single source of truth for API settings
 */

// API Endpoints - organized by module
export const API_ENDPOINTS = {
  objectStorage: {
    upload: "/file/upload",
    delete: "/file/delete",
  },
  auth: {
    signup: "/auth/signup",
    checkLogin: "/auth/login/check?identifier=",
    verifyLogin: "/auth/login/verify",
    logout: "/auth/logout",
    otpGenerate: "/auth/otp/generate",
    otpVerify: "/auth/otp/verify",
    forgotPassword: "/auth/forget",
    resetPassword: "/auth/reset",
    refreshToken: "/auth/refreshToken",
    googleOAuth: "/auth/oauth/google",
    googleOAuthCallback: "/auth/oauth/google/callback",
    googleOAuthAgentCallback: "/auth/oauth/google/agent/callback",
    microsoftOAuth: "/auth/oauth/microsoft",
    microsoftOAuthCallback: "/auth/oauth/microsoft/callback",
    microsoftOAuthAgentCallback: "/auth/oauth/microsoft/agent/callback",
    verifyTurnstile: "/auth/turnstile/submit-form",
    validateInvitation: "/auth/invitations/validate",
    acceptInvitation: "/auth/invitations/accept",
    agentSignup: "/auth/agent-signup",
    generateToken: "/auth/generate-token",
  },
  user: {
    getOrganizations: (email: string) => `/user/${encodeURIComponent(email)}`,
  },
  whatsapp: {
    accountDetails: "/whatsapp/account-details",
  },
  whatsappManagement: {
    qrCodes: {
      getAll: "/message_qrdls/getall",
      create: "/message_qrdls/create",
      update: "/message_qrdls/update",
      delete: "/message_qrdls/deleteqrcode",
    },
    consent: {
      getPolicy: "/whatsapp/consent/policy",
      upsertPolicy: "/whatsapp/consent/policy",
      getKeywords: "/whatsapp/consent/keywords",
      addKeyword: "/whatsapp/consent/keywords",
      deleteKeyword: (keyword: string) =>
        `/whatsapp/consent/keywords/${encodeURIComponent(keyword)}`,
      upsertAutoReplyMessage: (consentType: string) =>
        `/whatsapp/consent/messages/${consentType}`,
      getAutoReplyMessage: (consentType: string) =>
        `/whatsapp/consent/reply/${consentType}`,
      getAllMessages: "/whatsapp/consent/reply",
    },
    widget: {
      createOrUpdate: "/whatsapp-button-config/create-update",
      getAllByCIN: "/whatsapp-button-config/cin/",
      getById: "/whatsapp-button-config/get/",
      delete: "/whatsapp-button-config/button/",
    },
  },
  templates: {
    base: "/templates",
    byId: (templateId: number | string) => `/templates/${templateId}`,
    submitToMeta: "/templates/submit-to-meta",
    delete: "/templates/delete",
    uploadMediaResumable: "/upload-media/resumable",
    uploadMediaWhatsapp: "/upload-media",
    aiGenerate: "/templates/generate",
    library: "/template-library",
  },
  ctwaAds: {
    fetchMapping: "/instagram-ads/CTWAMapping/fetch",
    createOrUpdateMapping: "/instagram-ads/CTWAMapping/create",
    listPages: "/instagram-ads/pages",
    listAdAccounts: "/instagram-ads/ad-accounts",
    listCampaigns: "/instagram-ads/campaigns/list",
    insights: "/instagram-ads/insights",
    campaignRelatedServices: "/instagram-ads/campaign-related-services",
    targetSearch: "/instagram-ads/target-search",
    createCampaign: "/instagram-ads/campaign",
    createAdSet: "/instagram-ads/adset",
    createAdCreative: "/instagram-ads/adcreative",
    createAd: "/instagram-ads/ad",
    uploadAdMedia: "/instagram-ads/ad-images/upload",
    uploadAdVideo: "/instagram-ads/ad-videos/upload",
    oauthAccessToken: "/instagram-ads/oauth/access-token",
    delete: "/instagram-ads/delete",
  },
  userManagement: {
    roles: {
      base: "/roles",
      byId: (roleId: number | string) => `/roles/${roleId}`,
    },
    permissions: {
      system: "/permissions",
      rolePermissions: "/role-permissions",
      updateBulk: "/role-permissions/bulk",
    },
    teams: {
      base: "/teams",
      byId: (teamId: number | string) => `/teams/${teamId}`,
      users: (teamId: number | string) => `/teams/${teamId}/users`,
      manageUsers: (teamId: number | string) => `/teams/${teamId}/users/manage`,
    },
    users: {
      list: "/users/list",
      invitations: "/users/invitations",
      invitationById: (invitationId: number | string) =>
        `/users/invitations/${invitationId}`,
      resendInvitation: (invitationId: number | string) =>
        `/users/invitations/${invitationId}/resend`,
      updateStatus: (userId: number | string, status: string) =>
        `/user/${userId}/status?status=${status}`,
    },
    projects: {
      base: "/projects",
      byId: (projectId: number | string) => `/projects/${projectId}`,
      users: "/project/users",
      userProjects: "/user/projects/list",
      members: (projectId: number | string) =>
        `/projects/${projectId}/members`,
      changeUserRole: "/project/user/role/change",
    },
  },
  messageRules: {
    enquiry: "/rule/enquiry",
    create: "/rule/create",
    keywords: "/rule/keywords",
    quickRepliesEnquiry: "/web/quick-replies-enquiry",
    flowEnquiry: "/rule/flow/enquiry",
  },
  answerVariables: {
    fetch: "/rule/answer-variables/fetch",
    update: "/rule/answer-variables/update",
  },
  propertyManagement: {
    groups: "/groups",
    fields: "/fields",
    archiveProperty: "/properties/archive",
    deleteProperty: "/properties",
    bulkAssignToGroup: "/properties/groups/bulk",
    entityUsages: "/entity-usages",
  },
  conversations: {
    base: "/conversations",
    byId: (conversationId: number | string) =>
      `/conversations/${conversationId}`,
    history: (conversationId: number | string) =>
      `/conversations/${conversationId}/history`,
    status: (conversationId: number | string) =>
      `/conversations/${conversationId}/status`,
    assignAgent: (conversationId: number | string) =>
      `/conversations/${conversationId}/assign-agent`,
    comments: (conversationId: number | string) =>
      `/conversations/${conversationId}/comments`,
    commentById: (
      conversationId: number | string,
      commentId: number | string,
    ) => `/conversations/${conversationId}/comments/${commentId}`,
    deleteComment: (commentId: number | string) =>
      `/conversations/comments/${commentId}`,
    messages: "/messages",
    sendMessage: "/messages",
    typingIndicator: "/messages/whatsapp/typing-indicator",
    unreadReset: (conversationId: number | string) =>
      `/conversations/${conversationId}/unread-reset`,
  },
  emailSettings: {
    footers: "/v1/email-footers",
    domains: "/email/domains",
    domainById: (domainId: number | string) => `/email/domains/${domainId}`,
    verifyDomain: (domainId: number | string) =>
      `/email/domains/${domainId}/verify`,
    activateDomain: (domainId: number | string) =>
      `/email/domains/${domainId}/activate`,
    deleteDomain: (domainId: number | string) => `/email/domains/${domainId}`,
  },
  schema: {
    views: {
      base: "/views",
      byObjectCode: (objectCode: string) => `/views/${objectCode}`,
      byId: (viewId: number | string) => `/views/${viewId}`,
    },
  },
  campaigns: {
    search: "/campaigns/search",
    base: "/campaigns",
    byId: (campaignId: number | string) => `/campaigns/${campaignId}`,
    audiencePreview: "/campaigns/audience/preview",
    audience: (campaignId: number | string) =>
      `/campaigns/${campaignId}/audience`,
    hardDelete: (campaignId: number | string) =>
      `/campaigns/${campaignId}/hard`,
  },
} as const;

// Environment types
export const ENVIRONMENTS = {
  DEV: "dev",
  PROD: "prod",
} as const;

export const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";

// Environment-specific configurations
type Environment = (typeof ENVIRONMENTS)[keyof typeof ENVIRONMENTS];
const ENV: Environment =
  (import.meta.env.VITE_ENVIRONMENT as Environment) || ENVIRONMENTS.DEV;
const environmentConfigs = {
  [ENVIRONMENTS.DEV]: {
    APP_URL: "https://app-new-dev.notifyy.io",
    BASE_URL: "https://crm-dev.notifyy.io/v3/api/",
    OBJECT_STORAGE_URL: "https://object-dev.notifyy.io/v3/api/",
    CAMPAIGN_BASE_URL: "https://scheduler-dev.notifyy.io/v3/api/",
    CRM_URL: "https://crm-dev.notifyy.io/v3/api/",
    META_BASE_URL: "https://graph.facebook.com/v21.0/",
    WEB_SOCKET_URL: "wss://consumer-dev.notifyy.io/tss-websocket/websocket",
    NOTIFICATION_SOCKET_URL: "https://consumer-dev.notifyy.io/ws-notifications",
    WIDGET_SCRIPT_URL: "https://cdn.notifyy-widget.pages.dev/widget-plugin.js",
    DEBUG_MODE: true,
    API_TIMEOUT: 30000,
    RETRY_ATTEMPTS: 5,
    ENV_NAME: "Development",
  },
  [ENVIRONMENTS.PROD]: {
    APP_URL: "https://app.notifyy.io",
    BASE_URL: "https://crm.notifyy.io/v3/api/",
    OBJECT_STORAGE_URL: "https://object.notifyy.io/v3/api/",
    CAMPAIGN_BASE_URL: "https://scheduler.notifyy.io/v3/api/",
    CRM_URL: "https://crm.notifyy.io/v3/api/",
    META_BASE_URL: "https://graph.facebook.com/v21.0/",
    WEB_SOCKET_URL: "wss://consumer.notifyy.io/tss-websocket/websocket",
    NOTIFICATION_SOCKET_URL: "https://consumer.notifyy.io/ws-notifications",
    WIDGET_SCRIPT_URL: "https://cdn.notifyy-widget.pages.dev/widget-plugin.js",
    DEBUG_MODE: false,
    API_TIMEOUT: 30000,
    RETRY_ATTEMPTS: 5,
    ENV_NAME: "Production",
  },
} as const;

// Current environment config
const config = environmentConfigs[ENV];

// API Base URLs
export const API_URLS = {
  app: config.APP_URL,
  main: config.BASE_URL,
  objectStorage: config.OBJECT_STORAGE_URL,
  campaign: config.CAMPAIGN_BASE_URL,
  crm: config.CRM_URL,
  meta: config.META_BASE_URL,
  websocket: config.WEB_SOCKET_URL,
  notification: config.NOTIFICATION_SOCKET_URL,
  widgetScript: config.WIDGET_SCRIPT_URL,
} as const;

// API Settings
export const API_SETTINGS = {
  timeout: config.API_TIMEOUT,
  retries: config.RETRY_ATTEMPTS,
  debugMode: config.DEBUG_MODE,
  envName: config.ENV_NAME,
} as const;

// Auth Retry Configuration
export const AUTH_RETRY_CONFIG = {
  /** Maximum number of retry attempts before forcing logout */
  MAX_RETRIES: 7,
  /** Initial delay between retries in milliseconds */
  INITIAL_DELAY_MS: 1000,
  /** Maximum delay between retries in milliseconds */
  MAX_DELAY_MS: 10000,
  /** Backoff multiplier for exponential delay */
  BACKOFF_MULTIPLIER: 1.5,
} as const;

// Error Messages
export const API_ERRORS = {
  NETWORK: "Unable to connect. Please check your internet.",
  TIMEOUT: "Request timed out. Please try again.",
  SERVER: "Server error. Please try again later.",
  UNAUTHORIZED: "Session expired. Please login again.",
  FORBIDDEN: "You do not have permission.",
  NOT_FOUND: "Resource not found.",
  RATE_LIMIT: "Too many requests. Please slow down.",
  UNKNOWN: "Something went wrong.",
} as const;

// Export types
export type ApiUrlKey = keyof typeof API_URLS;
export type EnvironmentConfig = typeof config;

// Export current environment
export const CURRENT_ENV = ENV;
export const IS_DEV = ENV === ENVIRONMENTS.DEV;
export const IS_PROD = ENV === ENVIRONMENTS.PROD;

// Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: "auth_token",
  REFRESH_TOKEN: "refresh_token",
  USER_DATA: "user_data",
  THEME: "theme",
  PERSIST_ROOT: "persist:root",
} as const;

// Security Configuration
export const SECURITY_CONFIG = {
  ENABLE_ENCRYPTION: true,
  TOKEN_STORAGE: "localStorage" as const,
} as const;

// Google OAuth Configuration
export const GOOGLE_OAUTH_CONFIG = {
  clientId:
    "746540176547-dq7ogmnjqmurhu9uppuamr2mfmjtbftt.apps.googleusercontent.com",
  redirectUri: `${API_URLS.main}${API_ENDPOINTS.auth.googleOAuthCallback.slice(1)}`,
  scope: "openid email profile",
  accessType: "offline",
  prompt: "consent",
} as const;

// Microsoft OAuth Configuration (MSAL)
export const MICROSOFT_OAUTH_CONFIG = {
  clientId: "32c1f1b1-b3e5-4197-88a2-24851c4bd92f",
  authority: "https://login.microsoftonline.com/common",
  redirectUri: `${API_URLS.main}${API_ENDPOINTS.auth.microsoftOAuthCallback.slice(1)}`,
  postLogoutRedirectUri: window.location.origin,
  scopes: ["openid", "profile", "email"],
  responseMode: "query",
} as const;

export const buildMicrosoftOAuthUrl = (
  frontendCallbackUrl?: string,
  fromPath?: string,
  useAgentCallback?: boolean,
): string => {
  const nonce =
    crypto.randomUUID?.() ||
    `${Math.random().toString(36).substring(2)}${Date.now().toString(36)}`;
  const state = JSON.stringify({
    nonce: nonce.substring(0, 15),
    ...(frontendCallbackUrl && { redirectUri: frontendCallbackUrl }),
    ...(fromPath && { from: fromPath }),
  });

  // Use agent callback redirect URI for invitation signup
  const redirectUri = useAgentCallback
    ? `${API_URLS.main}${API_ENDPOINTS.auth.microsoftOAuthAgentCallback.slice(1)}`
    : MICROSOFT_OAUTH_CONFIG.redirectUri;

  const params = new URLSearchParams({
    client_id: MICROSOFT_OAUTH_CONFIG.clientId,
    response_type: "code",
    redirect_uri: redirectUri,
    response_mode: MICROSOFT_OAUTH_CONFIG.responseMode,
    scope: MICROSOFT_OAUTH_CONFIG.scopes.join(" "),
    state,
    nonce,
  });

  return `${MICROSOFT_OAUTH_CONFIG.authority}/oauth2/v2.0/authorize?${params.toString()}`;
};

export const buildGoogleOAuthUrl = (
  frontendCallbackUrl?: string,
  fromPath?: string,
  useAgentCallback?: boolean,
): string => {
  // Use agent callback redirect URI for invitation signup
  const redirectUri = useAgentCallback
    ? `${API_URLS.main}${API_ENDPOINTS.auth.googleOAuthAgentCallback.slice(1)}`
    : GOOGLE_OAUTH_CONFIG.redirectUri;

  const nonce =
    crypto.randomUUID?.() ||
    `${Math.random().toString(36).substring(2)}${Date.now().toString(36)}`;

  const params = new URLSearchParams({
    client_id: GOOGLE_OAUTH_CONFIG.clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: GOOGLE_OAUTH_CONFIG.scope,
    access_type: GOOGLE_OAUTH_CONFIG.accessType,
    prompt: GOOGLE_OAUTH_CONFIG.prompt,
  });
  const stateObj: Record<string, string> = {
    nonce: nonce.substring(0, 15),
  };
  if (frontendCallbackUrl) {
    stateObj.redirectUri = frontendCallbackUrl;
  }
  if (fromPath) {
    stateObj.from = fromPath;
  }
  params.set("state", JSON.stringify(stateObj));
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
};
